import React, { ChangeEvent, useMemo } from 'react';
import { PInputTextProps, PInputTextPropsSeverity } from 'components/prime/PInputText/PInputText';
import { SearchStyle as S } from './Search.style';
import { PButtonIcon } from 'components/prime/PButton/PButtonIcon';
import { Icon } from 'components/Icon/Icon';

export const Search = React.forwardRef<
  React.ForwardRefExoticComponent<PInputTextProps & React.RefAttributes<HTMLInputElement>>,
  Omit<PInputTextProps, 'onChange' | 'severity'> & {
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    debounceTime?: number;
    severity?: PInputTextPropsSeverity | 'ghost';
  }
>((props, ref) => {
  const timeoutRef = React.useRef<NodeJS.Timeout>();
  const [innerVal, setInnerVal] = React.useState(props.value || '');
  const { debounceTime, onChange } = props;
  const debounceEnabled = typeof debounceTime === 'number';

  const cleanProps = useMemo(() => (({ debounceTime, ...o }: any) => o)(props), [props]);

  const innerOnChange = React.useCallback(
    (e: any) => {
      if (debounceEnabled) {
        setInnerVal(e.target.value);
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(
          () => onChange?.({ target: { value: e.target.value } } as any),
          debounceTime
        );
      } else {
        onChange?.(e);
      }
    },
    [debounceEnabled, onChange, debounceTime]
  );

  return (
    <S.InputText
      placeholder="Search"
      maxLength={255}
      {...cleanProps}
      value={debounceEnabled ? innerVal : props.value}
      onChange={(e) => innerOnChange(e)}
      leftIcon={<Icon icon="searchMdLine" />}
      rightIcon={
        (debounceEnabled && innerVal) || props.value ? (
          <PButtonIcon
            size="xs"
            shape="squircle"
            icon="xLine"
            onClick={(e: any) => {
              if (onChange) {
                const el: HTMLInputElement = e.target as any;
                el.value = '';
                innerOnChange(e as any);
                el.blur();
              }
            }}
            disabled={props.disabled}
          />
        ) : null
      }
      ref={ref as any}
      autoComplete="off"
    />
  );
});
